import React, { useEffect, useRef, useState } from 'react';
import { WorldCanvas, WorldCanvasHandle, Viewport } from './WorldCanvas/WorldCanvas';
import { GridOverlay } from './GridOverlay/GridOverlay';
import { SpeechBubbles } from './SpeechBubbles';
import { WebSocketClient, WsEvent, getWsBase } from '../api/websocket';
import { useSpeechBubbles } from '../hooks/useSpeechBubbles';
import { Point } from '../types/point';

interface BackgroundCanvasProps {
  /** CSS opacity of the canvas layer. Default 0.35. */
  opacity?: number;
}

/**
 * Renders actual game world points as a full-screen background layer.
 * Connects to the WebSocket as a guest observer at a randomly chosen
 * world area so every page shows a real (and different) slice of the map.
 *
 * - pointer-events: none  → never intercepts user interaction
 * - position: fixed       → stays behind all page content
 * - no zoom controls      → pure read-only view
 */
export const BackgroundCanvas: React.FC<BackgroundCanvasProps> = ({
  opacity = 0.35,
}) => {
  const [points, setPoints] = useState<Point[]>([]);
  const [windowSize, setWindowSize] = useState({
    width : window.innerWidth,
    height: window.innerHeight,
  });
  const wsRef          = useRef<WebSocketClient | null>(null);
  const worldCanvasRef = useRef<WorldCanvasHandle>(null);
  const { speeches, handleSaidEvent, clearSpeeches } = useSpeechBubbles();

  // Pick a stable random world centre once per mount.
  // Range –300 … +300 to explore different parts of the world.
  const [viewport] = useState<Viewport>(() => {
    // Promo capture points the backdrop at a chosen part of the world
    // ("x,y,zoom" in localStorage). Unset for everyone else — see video/README.md.
    const promo = localStorage.getItem('promoBg');
    if (promo) {
      const [x, y, zoom] = promo.split(',').map(Number);
      if ([x, y, zoom].every(n => Number.isFinite(n))) return { x, y, zoom };
    }
    return {
      x   : 0,//Math.round((Math.random() - 0.5) * 600),
      y   : 0,// Math.round((Math.random() - 0.5) * 600),
      zoom: 5,
    };
  });

  // ── Window resize ──────────────────────────────────────────────
  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Guest WS connection ────────────────────────────────────────
  useEffect(() => {
    const { x, y, zoom } = viewport;
    const { width: w, height: h } = windowSize;
    const halfW = Math.ceil(w / 2 / zoom) + 20;
    const halfH = Math.ceil(h / 2 / zoom) + 20;

    const area = `${x - halfW},${y - halfH},${x + halfW},${y + halfH}`;
    const url    = `${getWsBase()}?token=guest&area=${area}`;

    const client = new WebSocketClient(url, (event: WsEvent) => {
      if (event.type === 'init' || event.type === 'points_update') {
        setPoints(Array.isArray(event.points) ? event.points : []);
      } else if (event.type === 'said') {
        handleSaidEvent(event.point_id, event.text, event.style);
      } else if (event.type === 'jumped') {
        worldCanvasRef.current?.triggerJump(event.point_id);
      }
    });
    wsRef.current = client;

    return () => {
      client.close();
      wsRef.current = null;
      clearSpeeches();
    };
  }, [viewport, windowSize]); // windowSize is stable after first render

  return (
    <div
      style={{
        position      : 'fixed',
        inset         : 0,
        zIndex        : 0,
        opacity,
        pointerEvents : 'none',
        // Dim the colours a bit more with an extra dark multiply layer
        background    : 'rgba(0,0,0,0.25)',
      }}
    >
      <WorldCanvas
        ref={worldCanvasRef}
        points={points}
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        // onViewportChange intentionally omitted → read-only
      />
      <GridOverlay
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        visible
      />
      <SpeechBubbles
        speeches={speeches}
        points={points}
        viewport={viewport}
        windowSize={windowSize}
      />
    </div>
  );
};
